import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const TYPES = [
  { value: "openai_compatible", label: "OpenAI compatible" },
  { value: "gemini", label: "Gemini" },
  { value: "anthropic", label: "Anthropic" },
  { value: "custom", label: "API personalizada" },
];

const AUTH_STYLES = [
  { value: "bearer", label: "Bearer token" },
  { value: "header", label: "Header personalizado" },
  { value: "query", label: "Query param" },
];

const EMPTY = {
  id: "", name: "", type: "openai_compatible",
  base_url: "", api_key: "",
  auth_style: "bearer", auth_header_name: "Authorization", auth_query_param: "key",
  models: [], active: true, notes: "",
};

export default function AddGatewayEngineDialog({ engine, onClose, onSaved }) {
  const isEdit = !!engine;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [newModel, setNewModel] = useState({ id: "", name: "" });

  useEffect(() => {
    if (engine) {
      setForm({
        ...EMPTY,
        ...engine,
        api_key: "", // never prefill — user must retype if they want to change
      });
    } else {
      setForm(EMPTY);
    }
  }, [engine]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addModel = () => {
    if (!newModel.id.trim()) return;
    if (form.models.some((m) => m.id === newModel.id.trim())) {
      toast.error("Ya existe un modelo con ese id");
      return;
    }
    set({
      models: [...form.models, {
        id: newModel.id.trim(),
        name: newModel.name.trim() || newModel.id.trim(),
        notes: "",
      }],
    });
    setNewModel({ id: "", name: "" });
  };
  const removeModel = (id) => set({ models: form.models.filter((m) => m.id !== id) });

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const patch = {
          name: form.name, type: form.type, base_url: form.base_url,
          auth_style: form.auth_style,
          auth_header_name: form.auth_header_name,
          auth_query_param: form.auth_query_param,
          models: form.models, active: form.active, notes: form.notes,
        };
        if (form.api_key.trim()) patch.api_key = form.api_key.trim();
        await api.patch(`/gateway/engines/${engine.id}`, patch);
        toast.success("Motor actualizado");
      } else {
        if (!form.id.trim() || !form.name.trim() || !form.api_key.trim() || !form.base_url.trim()) {
          toast.error("Rellena id, nombre, base URL y API key");
          setSaving(false);
          return;
        }
        await api.post("/gateway/engines", { ...form, id: form.id.trim() });
        toast.success("Motor creado");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TID.gateway.engineDialog}
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar motor "${engine.id}"` : "Nuevo motor IA"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">ID interno</Label>
              <Input
                data-testid={TID.gateway.engineField("id")}
                value={form.id}
                onChange={(e) => set({ id: e.target.value.toLowerCase() })}
                disabled={isEdit}
                placeholder="openai-produccion"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Nombre</Label>
              <Input
                data-testid={TID.gateway.engineField("name")}
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="OpenAI Producción"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set({ type: v })}>
                <SelectTrigger data-testid={TID.gateway.engineField("type")} className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Auth style</Label>
              <Select value={form.auth_style} onValueChange={(v) => set({ auth_style: v })}>
                <SelectTrigger data-testid={TID.gateway.engineField("auth_style")} className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {AUTH_STYLES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.auth_style === "header" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Nombre del header</Label>
              <Input
                value={form.auth_header_name}
                onChange={(e) => set({ auth_header_name: e.target.value })}
                placeholder="x-api-key"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
          )}
          {form.auth_style === "query" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Nombre del query param</Label>
              <Input
                value={form.auth_query_param}
                onChange={(e) => set({ auth_query_param: e.target.value })}
                placeholder="key"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Base URL</Label>
            <Input
              data-testid={TID.gateway.engineField("base_url")}
              value={form.base_url}
              onChange={(e) => set({ base_url: e.target.value })}
              placeholder="https://api.openai.com/v1"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">
              API Key {isEdit && <span className="text-[10px] text-zinc-500">(deja vacío para conservar la actual)</span>}
            </Label>
            <Input
              data-testid={TID.gateway.engineField("api_key")}
              type="password"
              value={form.api_key}
              onChange={(e) => set({ api_key: e.target.value })}
              placeholder={isEdit ? (engine.api_key_masked || "••••••••") : "sk-..."}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>

          {/* Models editor */}
          <div className="space-y-2 rounded-md border border-zinc-800 bg-black/20 p-3">
            <Label className="text-xs text-zinc-400">Modelos</Label>
            {form.models.length === 0 ? (
              <div className="text-[11px] text-zinc-500 italic">Aún no hay modelos.</div>
            ) : (
              <div className="space-y-1">
                {form.models.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded bg-zinc-900 px-2.5 py-1.5 text-xs">
                    <div className="min-w-0">
                      <div className="font-mono truncate">{m.id}</div>
                      {m.name !== m.id && <div className="text-[10px] text-zinc-500 truncate">{m.name}</div>}
                    </div>
                    <button
                      onClick={() => removeModel(m.id)}
                      className="text-zinc-500 hover:text-red-400 shrink-0 ml-2"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Input
                value={newModel.id}
                onChange={(e) => setNewModel({ ...newModel, id: e.target.value })}
                placeholder="model-id (p.ej. gpt-5)"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs h-8"
              />
              <Input
                value={newModel.name}
                onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                placeholder="nombre visible (opcional)"
                className="bg-zinc-900 border-zinc-800 text-xs h-8"
              />
              <Button
                data-testid={TID.gateway.engineAddModelBtn}
                onClick={addModel}
                size="sm"
                variant="outline"
                className="h-8 gap-1 bg-zinc-900 border-zinc-700 shrink-0"
              >
                <Plus className="h-3 w-3" /> Añadir
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400">Motor activo (administrativo)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            data-testid={TID.gateway.engineSubmit}
            onClick={submit}
            disabled={saving}
            size="sm"
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear motor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
